import React, { FormEvent, ChangeEvent, useState } from 'react';
import '../../App.css';
import { AuthService } from '../../APIService/AuthService';
import { useParams } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { Box, Button, MenuItem } from '@mui/material';
import country from '../../assets/country-codes.json';
import Avatar from '@mui/material/Avatar';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Container from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import { InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const regularExpression = /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/;

interface FormDataState {
    firstname: string;
    lastname: string;
    countrycode: string;
    phonenumber: string;
    password: string;
    password2: string;
}

interface CountryCode {
    dial_code: string;
    name: string;
}

export default function Registration(): JSX.Element {
    const params = useParams<{ key: string }>();
    const validationCode = params.key;

    const [formData, setFormData] = useState<FormDataState>({
        firstname: "",
        lastname: "",
        countrycode: "+1",
        phonenumber: "",
        password: "",
        password2: "",
    });

    const [isPasswordError, setPasswordError] = useState<boolean>(false);
    const [isPasswordFormatError, setPasswordFormatError] = useState<boolean>(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>('');
    const [countryCodes, setCountryCodes] = useState<CountryCode[]>(country.countryCodes);
    const [loading, setLoading] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [alertMesage, setAlertMesage] = useState<string>('');
    const [alertHeader, setAlertHeader] = useState<string>('');
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isHideForm, setIsHideForm] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        if (!regularExpression.test(formData.password)) {
            setPasswordFormatError(true);
            setPasswordErrorMessage("Password must contain one uppercase letter, one number, and one special character.");
        } else if (formData.password !== formData.password2) {
            setPasswordFormatError(false);
            setPasswordError(true);
            setPasswordErrorMessage("Passwords do not match.");
        } else {
            setPasswordFormatError(false);
            setPasswordError(false);
            if (!validationCode) return;
            if (!loading) setLoading(true);

            try {
                const validateResp: any = await AuthService.userRegistrationValidate({ validationcode: validationCode });

                if (!validateResp.success) {
                    setAlertHeader('Failed');
                    setAlertMesage('Registration failed. Please try again.');
                    setOpen(true);
                    return;
                }

                const registrationResp: any = await AuthService.userRegistration({
                    validationcode: validationCode,
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    phone: `${formData.countrycode}-${formData.phonenumber}`,
                    password: formData.password,
                });

                if (registrationResp.success) {
                    setOpen(true);
                    setIsSuccess(true);
                    setAlertHeader('Registration Success');
                    setAlertMesage('Registration successful.');
                } else {
                    const msg =
                        (typeof registrationResp.data === 'string' && registrationResp.data) ||
                        (registrationResp.errorResponse as { errorDescription?: string } | undefined)?.errorDescription ||
                        'Registration failed.';
                    setOpen(true);
                    setAlertHeader('Failed');
                    setAlertMesage(msg);
                }
            } catch (error) {
                setOpen(true);
                setAlertHeader('Failed');
                setAlertMesage('Registration failed. Please try again.');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleClose = (): void => {
        if (isSuccess) {
            setIsHideForm(true);
        }
        setOpen(false);
    };

    if (isHideForm) {
        return (
            <Container component="main" maxWidth="sm">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        boxShadow: 3,
                        borderRadius: 2,
                        px: 4,
                        py: 6,
                    }}
                >
                    <h4>Registration completed. Now you can login to smart filter controller mobile application.</h4>
                </Box>
            </Container>
        );
    }

    return (
        <div>
            <Container component="main" maxWidth="sm">
                <CssBaseline />
                <Box
                    sx={{
                        marginTop: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        boxShadow: 3,
                        borderRadius: 2,
                        px: 4,
                        py: 6,
                    }}
                >
                    <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                        <AccountCircleIcon />
                    </Avatar>
                    <Typography component="h1" variant="h5">
                        User Registration
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ mt: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        autoComplete="given-name"
                                        name="firstname"
                                        required
                                        fullWidth
                                        id="firstname"
                                        label="First Name"
                                        variant="standard"
                                        autoFocus
                                        onChange={handleChange}
                                        value={formData.firstname}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="lastname"
                                        label="Last Name"
                                        name="lastname"
                                        variant="standard"
                                        onChange={handleChange}
                                        value={formData.lastname}
                                        autoComplete="family-name"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="countrycode"
                                        name="countrycode"
                                        select
                                        fullWidth
                                        label="Country Code"
                                        variant="standard"
                                        onChange={handleChange}
                                        value={formData.countrycode}
                                        SelectProps={{
                                            MenuProps: {
                                                anchorOrigin: {
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                },
                                                // getContentAnchorEl: null,
                                            },
                                        }}
                                    >
                                        {countryCodes.map((option) => (
                                            <MenuItem key={option.dial_code} value={option.dial_code}>
                                                {`${option.dial_code} ${option.name}`}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        id="phonenumber"
                                        fullWidth
                                        name="phonenumber"
                                        label="Phone Number"
                                        type="number"
                                        inputProps={{ maxLength: 11 }}
                                        variant="standard"
                                        required
                                        onChange={handleChange}
                                        value={formData.phonenumber}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        name="password"
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        id="password"
                                        error={isPasswordFormatError}
                                        helperText={isPasswordFormatError ? passwordErrorMessage : ''}
                                        variant="standard"
                                        autoComplete="new-password"
                                        onChange={handleChange}
                                        value={formData.password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={(): void => setShowPassword((prev) => !prev)}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        required
                                        fullWidth
                                        error={isPasswordError}
                                        helperText={isPasswordError ? passwordErrorMessage : ''}
                                        name="password2"
                                        label="Confirm Password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        id="password2"
                                        variant="standard"
                                        autoComplete="cnf-password"
                                        onChange={handleChange}
                                        value={formData.password2}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={(): void => setShowConfirmPassword((prev) => !prev)}
                                                        edge="end"
                                                        size="small"
                                                    >
                                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Box>
                                <Button
                                    type="submit"
                                    value="submit"
                                    fullWidth
                                    disabled={loading}
                                    variant="contained"
                                    sx={{ mt: 5, mb: 1, height: 40 }}
                                >
                                    {loading && (
                                        <CircularProgress
                                            size={24}
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                marginTop: '-12px',
                                                marginLeft: '-12px',
                                            }}
                                        />
                                    )}
                                    {loading ? '' : 'Register'}
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </Box>
            </Container>
            <Dialog
                open={open}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{alertHeader}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">{alertMesage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>
        </div >
    );
}
